/**
 * Preferințe tabel containere (filtre, sortare, coloane, paginare),
 * stocate în localStorage pe direcție (import / export separat).
 */

export type ContainersSortKey =
  | 'no'
  | 'company'
  | 'shipper'
  | 'booking'
  | 'route'
  | 'vessel'
  | 'onBoard'
  | 'eta'
  | 'oceanFreight'
  | 'status'
  | 'note';

export type ContainersSortDir = 'asc' | 'desc';

/** Coloane configurabile (ordinea default). `actions` e mereu la final, neconfigurabilă. */
export type ContainerColumnId =
  | 'no'
  | 'company'
  | 'shipper'
  | 'booking'
  | 'route'
  | 'vessel'
  | 'onBoard'
  | 'eta'
  | 'oceanFreight'
  | 'status'
  | 'note';

export interface ContainerColumnState {
  id: ContainerColumnId;
  visible: boolean;
}

export interface ContainersTablePrefs {
  statusFilter: string;
  search: string;
  sortKey: ContainersSortKey;
  sortDir: ContainersSortDir;
  page: number;
  pageSize: number;
  columns: ContainerColumnState[];
}

export type ContainersDirection = 'import' | 'export';

/** Import: include Ocean Freight (navlu ofertat), ca la conosamente. */
export const DEFAULT_CONTAINER_COLUMNS_IMPORT: ContainerColumnState[] = [
  { id: 'no', visible: true },
  { id: 'company', visible: true },
  { id: 'shipper', visible: true },
  { id: 'booking', visible: true },
  { id: 'route', visible: true },
  { id: 'vessel', visible: true },
  { id: 'onBoard', visible: true },
  { id: 'eta', visible: true },
  { id: 'oceanFreight', visible: true },
  { id: 'note', visible: true },
  { id: 'status', visible: true },
];

/** Export: fără Ocean Freight (coloana nu există pe export). */
export const DEFAULT_CONTAINER_COLUMNS_EXPORT: ContainerColumnState[] = [
  { id: 'no', visible: true },
  { id: 'company', visible: true },
  { id: 'shipper', visible: true },
  { id: 'booking', visible: true },
  { id: 'route', visible: true },
  { id: 'vessel', visible: true },
  { id: 'onBoard', visible: true },
  { id: 'eta', visible: true },
  { id: 'note', visible: true },
  { id: 'status', visible: true },
];

/** @deprecated folosește defaultContainerColumns(direction) */
export const DEFAULT_CONTAINER_COLUMNS = DEFAULT_CONTAINER_COLUMNS_IMPORT;

export function defaultContainerColumns(direction: ContainersDirection): ContainerColumnState[] {
  const src =
    direction === 'export' ? DEFAULT_CONTAINER_COLUMNS_EXPORT : DEFAULT_CONTAINER_COLUMNS_IMPORT;
  return src.map((c) => ({ ...c }));
}

export function defaultContainersPrefs(direction: ContainersDirection): ContainersTablePrefs {
  return {
    statusFilter: 'all',
    search: '',
    sortKey: 'eta',
    sortDir: 'asc',
    page: 1,
    pageSize: 10,
    columns: defaultContainerColumns(direction),
  };
}

export const DEFAULT_CONTAINERS_PREFS: ContainersTablePrefs = defaultContainersPrefs('import');

const STORAGE_PREFIX = 'tls-client.containers.v1';

export function containersPrefsKey(direction: 'import' | 'export'): string {
  return `${STORAGE_PREFIX}.${direction}`;
}

const SORT_KEYS: ContainersSortKey[] = [
  'no',
  'company',
  'shipper',
  'booking',
  'route',
  'vessel',
  'onBoard',
  'eta',
  'oceanFreight',
  'status',
  'note',
];

const PAGE_SIZES = [10, 20, 50, 100];

// Coloanele salvate care nu mai există (ex. „progress”, scoasă pe 05.10.2026) sunt ignorate mai jos.
function normalizeColumns(raw: unknown, direction: ContainersDirection): ContainerColumnState[] {
  const defaults = defaultContainerColumns(direction);
  const byId = new Map(defaults.map((c) => [c.id, c]));
  if (!Array.isArray(raw)) return defaults;
  const seen = new Set<ContainerColumnId>();
  const result: ContainerColumnState[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const id = (item as { id?: string }).id as ContainerColumnId;
    if (!byId.has(id) || seen.has(id)) continue;
    seen.add(id);
    const visible = (item as { visible?: boolean }).visible;
    result.push({
      id,
      visible: typeof visible === 'boolean' ? visible : true,
    });
  }
  // Completează coloanele lipsă (versiuni vechi) — în poziția lor implicită,
  // ca o coloană nouă (ex. Ocean Freight) să apară unde trebuie, nu la final.
  for (let i = 0; i < defaults.length; i += 1) {
    const def = defaults[i];
    if (seen.has(def.id)) continue;
    const prev = i > 0 ? result.findIndex((c) => c.id === defaults[i - 1].id) : -1;
    result.splice(prev + 1, 0, { ...def });
  }
  // Cel puțin o coloană vizibilă.
  if (!result.some((c) => c.visible)) {
    result[0] = { ...result[0], visible: true };
  }
  return result;
}

export function loadContainersPrefs(
  direction: ContainersDirection,
): ContainersTablePrefs {
  const fallback = defaultContainersPrefs(direction);
  try {
    const raw = localStorage.getItem(containersPrefsKey(direction));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<ContainersTablePrefs>;
    const rawSort = parsed.sortKey as ContainersSortKey;
    // Ocean Freight nu există pe export — nu lăsa o sortare salvată pe ea.
    const sortKey =
      SORT_KEYS.includes(rawSort) && !(direction === 'export' && rawSort === 'oceanFreight')
        ? rawSort
        : fallback.sortKey;
    const sortDir = parsed.sortDir === 'desc' ? 'desc' : 'asc';
    const pageSize = PAGE_SIZES.includes(Number(parsed.pageSize))
      ? Number(parsed.pageSize)
      : DEFAULT_CONTAINERS_PREFS.pageSize;
    const page =
      typeof parsed.page === 'number' && parsed.page >= 1
        ? Math.floor(parsed.page)
        : 1;
    return {
      statusFilter:
        typeof parsed.statusFilter === 'string' && parsed.statusFilter
          ? parsed.statusFilter
          : 'all',
      search: typeof parsed.search === 'string' ? parsed.search : '',
      sortKey,
      sortDir,
      page,
      pageSize,
      columns: normalizeColumns(parsed.columns, direction),
    };
  } catch {
    return fallback;
  }
}

export function saveContainersPrefs(
  direction: 'import' | 'export',
  prefs: ContainersTablePrefs,
): void {
  try {
    localStorage.setItem(containersPrefsKey(direction), JSON.stringify(prefs));
  } catch {
    // Quota / private mode — ignorăm.
  }
}

export function columnLabel(
  id: ContainerColumnId,
  lang: 'ro' | 'en',
  etaLabel: string,
): string {
  const isRo = lang === 'ro';
  switch (id) {
    case 'no':
      return isRo ? 'Container' : 'Container';
    case 'company':
      return isRo ? 'Firmă' : 'Company';
    case 'shipper':
      return 'Shipper';
    case 'booking':
      return 'Booking';
    case 'route':
      return isRo ? 'Rută' : 'Route';
    case 'vessel':
      return isRo ? 'Navă' : 'Vessel';
    case 'onBoard':
      return 'On Board Date';
    case 'eta':
      return etaLabel;
    case 'oceanFreight':
      return 'Ocean Freight';
    case 'status':
      return isRo ? 'Status' : 'Status';
    case 'note':
      return isRo ? 'Notă' : 'Note';
    default:
      return id;
  }
}

/** Mută o coloană în listă (reordonare). */
export function moveColumn(
  columns: ContainerColumnState[],
  index: number,
  delta: -1 | 1,
): ContainerColumnState[] {
  const next = [...columns];
  const j = index + delta;
  if (j < 0 || j >= next.length) return columns;
  const tmp = next[index];
  next[index] = next[j];
  next[j] = tmp;
  return next;
}

export function toggleColumnVisible(
  columns: ContainerColumnState[],
  id: ContainerColumnId,
): ContainerColumnState[] {
  const visibleCount = columns.filter((c) => c.visible).length;
  return columns.map((c) => {
    if (c.id !== id) return c;
    // Nu permite ascunderea ultimei coloane.
    if (c.visible && visibleCount <= 1) return c;
    return { ...c, visible: !c.visible };
  });
}
